import api, { ApiResponse } from './api';

export interface Sport {
  id: number;
  dazn_asset_id: string | null;
  name: string;
  icon: string | null;
  sort_order: number;
  competitions_count: number;
  created_at: string;
  competitions?: Competition[];
}

export interface Resolution {
  id: number;
  name: string;
  xaccel_value: string;
  sort_order: number;
  created_at: string;
}

export interface Competition {
  id: number;
  dazn_asset_id: string | null;
  sport_id: number;
  name: string;
  xaccel_server_id: number | null;
  server_ids?: string | null;
  is_active: number;
  channel_prefix: string | null;
  cdn_blacklist: string | null;
  cdn_preferred: string | null;
  auto_stop_minutes: number;
  stop_margin_minutes: number;
  num_channels: number;
  xaccel_user_agent?: string;
  channel_mode: 'numbered' | 'team' | 'both';
  video_map_overrides?: string | null;
  proxy_by_resolution?: string | null;
  special_events_mapping?: string | null;
  resolutions?: Resolution[];
  sport_name?: string;
  xaccel_server_name?: string;
  teams?: Team[];
  created_at: string;
  updated_at: string;
}

export interface Team {
  id: number;
  dazn_asset_id: string | null;
  competition_id: number | null;
  name: string;
  short_name: string | null;
  logo_url: string | null;
  competition_name?: string;
  sport_name?: string;
  created_at: string;
}

// Team Wizard types
export interface WizardTeam {
  name: string;
  dazn_asset_id?: string;
  logo_url?: string;
  is_existing: boolean;
}

// Wizard types
export interface WizardCompetition {
  competition_name: string;
  competition_asset_id: string;
  is_existing: boolean;
  image: string | null;
}

export interface WizardSport {
  sport_name: string;
  sport_asset_id: string;
  is_existing: boolean;
  existing_competitions_count: number;
  competitions: WizardCompetition[];
}

export interface WizardData {
  sports: WizardSport[];
}

export interface WizardImportResult {
  sports: Sport[];
  competitions: Competition[];
}

// EPG Event for filters
export interface EpgEvent {
  asset_id: string;
  title: string;
  start_time: string;
  start_time_local: string;
  date: string;
  date_formatted: string;
  description: string;
}

export interface EpgEventsResponse {
  events: EpgEvent[];
  meta: {
    competition_name: string;
    timezone: string;
    date_range: { start: string; end: string };
    total_events: number;
  };
}

export const sportsService = {
  async list(): Promise<Sport[]> {
    const response = await api.get<ApiResponse<Sport[]>>('/sports');
    return response.data.data;
  },

  async get(id: number): Promise<Sport> {
    const response = await api.get<ApiResponse<Sport>>(`/sports/${id}`);
    return response.data.data;
  },

  async create(data: { dazn_asset_id?: string; name: string; sort_order?: number }): Promise<Sport> {
    const response = await api.post<ApiResponse<Sport>>('/sports', data);
    return response.data.data;
  },

  async update(id: number, data: { name?: string; dazn_asset_id?: string; sort_order?: number }): Promise<Sport> {
    const response = await api.put<ApiResponse<Sport>>(`/sports/${id}`, data);
    return response.data.data;
  },

  async delete(id: number): Promise<void> {
    await api.delete(`/sports/${id}`);
  },

  async deleteAll(): Promise<void> {
    await api.delete('/sports/all');
  },

  async reorder(order: number[]): Promise<Sport[]> {
    const response = await api.put<ApiResponse<Sport[]>>('/sports/reorder', { order });
    return response.data.data;
  },

  async importWizard(sports: Array<{ dazn_asset_id?: string; name: string; icon?: string }>): Promise<{ data: Sport[]; message: string }> {
    const response = await api.post<ApiResponse<Sport[]> & { message: string }>('/sports/import-wizard', { sports });
    return { data: response.data.data, message: response.data.message || '' };
  },

  // Wizard methods
  async getWizardData(): Promise<WizardData> {
    const response = await api.get<ApiResponse<WizardData>>('/sports/wizard');
    return response.data.data;
  },

  async importFromWizard(
    sports: Array<{ sport_asset_id: string; sport_name: string }>,
    competitions: Array<{
      sport_asset_id: string;
      competition_asset_id: string;
      competition_name: string;
      image?: string | null;
    }>,
    /** Servers the competitions start with (ticked); empty = the default server. Nothing is created on them here. */
    serverIds: number[] = [],
  ): Promise<{ data: WizardImportResult; message: string }> {
    const response = await api.post<ApiResponse<WizardImportResult> & { message: string }>(
      '/sports/import-wizard',
      { sports, competitions, ...(serverIds.length > 0 ? { server_ids: serverIds } : {}) }
    );
    return { data: response.data.data, message: response.data.message || '' };
  },
};

export const competitionsService = {
  async list(params?: { sport_id?: number }): Promise<Competition[]> {
    const response = await api.get<ApiResponse<Competition[]>>('/competitions', { params });
    return response.data.data;
  },

  async get(id: number): Promise<Competition> {
    const response = await api.get<ApiResponse<Competition>>(`/competitions/${id}`);
    return response.data.data;
  },

  async create(data: {
    dazn_asset_id?: string;
    sport_id: number;
    name: string;
    xaccel_server_id?: number;
    server_ids?: number[];
    channel_mode?: 'numbered' | 'team' | 'both';
    channel_prefix?: string;
    auto_stop_minutes?: number;
    stop_margin_minutes?: number;
    num_channels?: number;
    is_active?: boolean;
    resolution_ids?: number[];
  }): Promise<Competition> {
    const response = await api.post<ApiResponse<Competition>>('/competitions', data);
    return response.data.data;
  },

  async update(id: number, data: {
    dazn_asset_id?: string;
    sport_id?: number;
    name?: string;
    xaccel_server_id?: number;
    server_ids?: number[];
    channel_mode?: 'numbered' | 'team' | 'both';
    channel_prefix?: string;
    cdn_blacklist?: string;
    cdn_preferred?: string;
    auto_stop_minutes?: number;
    stop_margin_minutes?: number;
    num_channels?: number;
    xaccel_user_agent?: string | null;
    is_active?: boolean;
    resolution_ids?: number[];
    video_map_overrides?: string | null;
    special_events_mapping?: string | null;
  }): Promise<Competition> {
    const response = await api.put<ApiResponse<Competition>>(`/competitions/${id}`, data);
    return response.data.data;
  },

  async delete(id: number): Promise<{ channelsLeft: Array<{ name: string; server: string }> }> {
    // The server needs the explicit confirmation (the page asked first); the competition's channels stay on the servers.
    const response = await api.delete<ApiResponse<{ channelsLeft: Array<{ name: string; server: string }> }>>(`/competitions/${id}`, { params: { confirm: true } });
    return response.data.data ?? { channelsLeft: [] };
  },

  async deleteAll(): Promise<void> {
    await api.delete('/competitions/all', { params: { confirm: true } });
  },

  async getEpgEvents(id: number): Promise<EpgEventsResponse> {
    const response = await api.get<ApiResponse<EpgEvent[]> & { meta: EpgEventsResponse['meta'] }>(`/competitions/${id}/epg-events`);
    return {
      events: response.data.data,
      meta: response.data.meta,
    };
  },
};

export const teamsService = {
  async list(params?: { competition_id?: number; sport_id?: number }): Promise<Team[]> {
    const response = await api.get<ApiResponse<Team[]>>('/teams', { params });
    return response.data.data;
  },

  async get(id: number): Promise<Team> {
    const response = await api.get<ApiResponse<Team>>(`/teams/${id}`);
    return response.data.data;
  },

  async create(data: {
    dazn_asset_id?: string;
    competition_id?: number;
    name: string;
    short_name?: string;
    logo_url?: string;
  }): Promise<Team> {
    const response = await api.post<ApiResponse<Team>>('/teams', data);
    return response.data.data;
  },

  async update(id: number, data: Partial<Team>): Promise<Team> {
    const response = await api.put<ApiResponse<Team>>(`/teams/${id}`, data);
    return response.data.data;
  },

  async delete(id: number): Promise<void> {
    await api.delete(`/teams/${id}`);
  },

  async importWizard(teams: Array<{ dazn_asset_id?: string; name: string; short_name?: string; logo_url?: string }>, competition_id?: number): Promise<{ data: Team[]; message: string }> {
    const response = await api.post<ApiResponse<Team[]> & { message: string }>('/teams/import-wizard', { teams, competition_id });
    return { data: response.data.data, message: response.data.message || '' };
  },

  async getWizardData(competitionId: number): Promise<WizardTeam[]> {
    const response = await api.get<ApiResponse<{ teams: WizardTeam[] }>>('/teams/wizard', {
      params: { competition_id: competitionId },
    });
    return response.data.data.teams;
  },

  async deleteByCompetition(competitionId: number): Promise<string> {
    const response = await api.delete<ApiResponse<null> & { message: string }>(`/teams/by-competition/${competitionId}`);
    return response.data.message || 'Team eliminati';
  },
};

export const resolutionsService = {
  async list(): Promise<Resolution[]> {
    const response = await api.get<ApiResponse<Resolution[]>>('/resolutions');
    return response.data.data;
  },

  async get(id: number): Promise<Resolution> {
    const response = await api.get<ApiResponse<Resolution>>(`/resolutions/${id}`);
    return response.data.data;
  },

  async create(data: { name: string; xaccel_value: string }): Promise<Resolution> {
    const response = await api.post<ApiResponse<Resolution>>('/resolutions', data);
    return response.data.data;
  },

  async update(id: number, data: { name?: string; xaccel_value?: string }): Promise<Resolution> {
    const response = await api.put<ApiResponse<Resolution>>(`/resolutions/${id}`, data);
    return response.data.data;
  },

  async delete(id: number): Promise<void> {
    await api.delete(`/resolutions/${id}`);
  },

  async reorder(order: number[]): Promise<Resolution[]> {
    const response = await api.put<ApiResponse<Resolution[]>>('/resolutions/reorder', { order });
    return response.data.data;
  },
};
